import cn from 'classnames'
import AlertBanner from 'ext-ui/components/AlertBanner/AlertBanner'
import { yellow800 } from 'ext-ui/style/colors'
import AlertCircleIcon from 'ext-ui/svg/icon/AlertCircleIcon'
import React, { ReactElement } from 'react'
import DynamicPricingTierCard from 'src/app/Onboarding/DynamicPricingTierCard'
import { SupportedBrands } from 'src/enums'
import { useTranslate } from 'src/localization/Trns'
import { PricingPlan } from 'src/queries/organizations/pricingPlans/pricingPlans.types'

import style from './DynamicSaasOnboardingPricingTiers.style'

export type DynamicSaasOnboardingPricingTiersProps = {
  pricingPlans: PricingPlan[]
  showVirtualCardAlert?: boolean
  isNonExtendIssuer?: boolean
  partnerName?: string
  className?: string
}

function getPartnerLogo(partner: string): React.ReactElement | undefined {
  return (
    <img
      alt={partner}
      className={cn('BrandPartnership-logo', partner)}
      src={`https://brand-assets.paywithextend.com/${partner}/BankLogoIcon.svg`}
    />
  )
}

export default function DynamicSaasOnboardingPricingTiers(
  props: DynamicSaasOnboardingPricingTiersProps,
): ReactElement {
  const {
    pricingPlans,
    showVirtualCardAlert = false,
    isNonExtendIssuer = false,
    partnerName,
    className = '',
  } = props
  const translate = useTranslate()

  return (
    <div
      className={cn(
        'DynamicSaasOnboardingPricingTiers',
        className,
        'flex-column gap-10 align-center',
      )}
      css={style}
    >
      <div className="HeadingText-container flex-column align-center justify-center gap-4">
        <h2 className="HeadingText">
          {translate("Choose the plan that's right for you")}
        </h2>
        <p className="SubText">
          {translate(
            'Find the right fit for your business. Start free or upgrade to a paid plan to unlock more powerful spend controls and expense tracking features.',
          )}
        </p>
      </div>
      {isNonExtendIssuer && partnerName && (
        <div className="flex-row justify-center align-center gap-5">
          {getPartnerLogo(partnerName.toLowerCase())}
          {getPartnerLogo(SupportedBrands.Extend)}
        </div>
      )}
      {showVirtualCardAlert && (
        <AlertBanner
          className="BrandPartnership-alert"
          variant="warning"
          data-testid="virtual-card-alert"
          icon={<AlertCircleIcon fill={yellow800} />}
          title={translate('Virtual cards unavailable with current bank')}
        >
          <p>
            {translate(
              'Your bank does not have integration with Extend. Partner banks enable secure, real-time virtual card creation.',
            )}
          </p>
          <a
            data-testid={'contact-us-link'}
            aria-label={translate('Contact us to learn more')}
            href={'https://www.paywithextend.com/support-request'}
            className={cn('BrandPartnership-alert-link')}
            target={'_blank'}
            rel={'noopener noreferrer'}
          >
            <p>{translate('Contact us to learn more')}</p>
          </a>
        </AlertBanner>
      )}
      <div className="PricingCards-container">
        {pricingPlans.map((plan, index) => (
          <DynamicPricingTierCard
            key={plan.id}
            plan={plan}
            planIndex={index}
            previousPlanName={
              index > 0 ? pricingPlans[index - 1].displayName : undefined
            }
            isNonExtendIssuer={isNonExtendIssuer}
          />
        ))}
      </div>
    </div>
  )
}
